import React, {useCallback} from "react";
import {useStores} from "../../../../stores/useStores";
import ListNoticesTemplate from "../../../../components/templates/notices/listNoticesTemplate";
import {observer} from "mobx-react-lite";
import {useAsync} from "react-async";
import NoticesDataListItem from "../../../../stores/domainStore/pages/models/noticesDataListItem";
import {NoticeStatus} from "../../../../shared/enums/noticeStatus";
import {ToastMessageStatus} from "../../../../components/atoms/toastMessage/toastMessage";
import {useTranslation} from "react-i18next";
import {OptionMenuKeyValue} from "../../../../stores/observables/optionMenuKeyValue";
import {
  excludeHeightForNotices,
  heightOfDataRow
} from "../../../../shared/constants";
import useWindowDimensions from "../../../../utils/ui/useWindowDimensions";
import {runInAction} from "mobx";

export interface IListNoticesProps {
  switchToAddNotice: () => void;
  switchToEditNotice: (noticeId?: string) => Promise<void>;
  switchToViewNotice: (noticeId?: string) => void;
}

const ListNotices: React.FC<IListNoticesProps> = ({
  switchToAddNotice,
  switchToEditNotice,
  switchToViewNotice
}) => {
  const {
    domainStore: {noticesDomainStore},
    uiStore: {noticesUiStore, modalStore, toastMessageStore, accountStore}
  } = useStores();

  const {height} = useWindowDimensions();
  noticesUiStore.currentNoticeListSizePerPage = Math.round(
    (height - excludeHeightForNotices) / heightOfDataRow
  );

  const {t} = useTranslation(["translation", "notice"]);

  const loadNoticesCallback = useCallback(async () => {
    await noticesUiStore.navigateNoticesPage(
      noticesUiStore.currentNoticeListPageNumber,
      noticesUiStore.currentNoticeListSizePerPage,
      noticesUiStore.currentNoticeListSortField,
      noticesUiStore.currentNoticeListSortOrder
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadNotices = useAsync({
    promiseFn: loadNoticesCallback,
    deferFn: (args) =>
      noticesUiStore.navigateNoticesPage(args[0], args[1], args[2], args[3]),
    onReject: (error) => modalStore.openErrorModal(error)
  });

  const loadEditableNotice = useAsync({
    deferFn: (args) => noticesUiStore.selectEditableNotice(args[0])
  });

  /**
   * A list of actions that can be applied to the selected notice.
   */
  const actions = [
    new OptionMenuKeyValue("edit", t("translation:actions:edit"), false),
    new OptionMenuKeyValue(
      "unpublish",
      t("translation:actions:unpublish"),
      false
    ),
    new OptionMenuKeyValue("delete", t("translation:actions:delete"), true)
  ];

  /**
   * Selected notice title.
   */
  const getElectedNoticeTitle = () =>
    noticesDomainStore.selectedNotice.notice.trim() !== ""
      ? noticesDomainStore.selectedNotice.notice
      : t("notice:titles.untitledNotice");

  /**
   * Async hook to process the delete notice request outcome.
   */
  const deleteNotice = useAsync({
    deferFn: noticesDomainStore.deleteNotice,
    onReject: (error) => {
      toastMessageStore.displayToastMessage(
        t("notice:messages.deleteNoticeFailure", {
          title: getElectedNoticeTitle()
        }),
        ToastMessageStatus.Danger
      );
      reloadList();
    },
    onResolve: () => {
      toastMessageStore.displayToastMessage(
        t("notice:messages.deleteNoticeSuccess", {
          title: getElectedNoticeTitle()
        }),
        ToastMessageStatus.Success
      );
      reloadList();
    }
  });

  function reloadList() {
    loadNotices.run(
      noticesUiStore.currentNoticeListPageNumber,
      noticesUiStore.currentNoticeListSizePerPage,
      noticesUiStore.currentNoticeListSortField,
      noticesUiStore.currentNoticeListSortOrder
    );
  }

  function GetUnpublishedContent() {
    let notice = noticesDomainStore.noticesList.find(
      (value) => value.id === selectedNotice.id
    );
    if (!notice || !notice.isMultiVillage) {
      return <>{t("notice:messages.unpublishWithoutChanges")}</>;
    }

    return (
      <>
        <p className={"font-weight-semi-bold"}>
          {t("notice:messages.unPublishNoticeAllVillageWarning")}
        </p>
        <p>{t("notice:messages.unpublishWithoutChanges")}</p>
      </>
    );
  }

  const unpublishNotice = useAsync({
    deferFn: (args) => noticesDomainStore.unpublishNotice(args[0]),
    onReject: (error) => {
      toastMessageStore.displayToastMessage(
        t("notice:messages.unpublishFailure", {
          title: noticesDomainStore.selectedNotice.notice
        }),
        ToastMessageStatus.Danger
      );
      reloadList();
    },
    onResolve: (title: string) => {
      toastMessageStore.displayToastMessage(
        t("notice:messages.unpublishSuccess", {title}),
        ToastMessageStatus.Success
      );
      reloadList();
    }
  });

  /**
   * Action to be applied to the selected notice.
   * @param action
   */
  const onActionSelected = async (action: OptionMenuKeyValue) => {
    switch (action.value) {
      case "delete": {
        modalStore.openModalWithContent({
          content: t("notice:messages.deleteNotice"),
          title: t("notice:titles.deleteNotice"),
          primaryButtonText: t("translation:confirmations.affirmativeDelete"),
          secondaryButtonText: t("translation:confirmations.negative"),
          centered: true,
          dangerButton: true,
          onPrimaryClick: () => {
            noticesUiStore.isListLoading = true;
            deleteNotice.run();
            modalStore.toggle();
          },
          onSecondaryClick: modalStore.toggle
        });
        break;
      }
      case "edit": {
        //open edit from here
        noticesUiStore.enableEditFromList();
        await switchToEditNotice(selectedNotice.id);
        break;
      }
      case "unpublish":
        modalStore.openModalWithContent({
          content: GetUnpublishedContent(),
          title: t("notice:titles.unpublish"),
          primaryButtonText: t("translation:actions.unpublish"),
          secondaryButtonText: t("translation:confirmations.negative"),
          centered: true,
          onPrimaryClick: () => {
            unpublishNotice.run(selectedNotice.id);
            modalStore.toggle();
          },
          onSecondaryClick: modalStore.toggle
        });
        break;
    }
  };

  /**
   * The selected notice to apply an action to.
   */
  const selectedNotice: NoticesDataListItem = noticesDomainStore.selectedNotice;

  /**
   * Filters actions list depends on the status of the notice.
   * @param actions - A list of actions to be filtered.
   * @param notice - To which the actions can be applied to.
   */
  const actionFilterFn = (
    actions: OptionMenuKeyValue[],
    notice: NoticesDataListItem
  ) => {
    // Non-multi village Admins users should not be allowed to un publish or edit multi village notices
    if (
      notice.isMultiVillage &&
      !accountStore.abilities.can("Update", "MultiVillage")
    ) {
      return [];
    }

    // Users without announcement delete permission are not able to perform actions on announcement
    if (
      notice.isAnnouncement &&
      !accountStore.abilities.can("Delete", "Announcement")
    ) {
      return [];
    }

    switch (notice.status) {
      case NoticeStatus.Draft:
      case NoticeStatus.Scheduled:
        return actions.filter(
          (action) =>
            action.value.includes("delete") || action.value.includes("edit")
        );
      case NoticeStatus.Published:
        return actions.filter(
          (action) =>
            action.value.includes("edit") || action.value.includes("unpublish")
        );
      default:
        return [];
    }
  };

  return (
    <ListNoticesTemplate
      isNavigating={noticesUiStore.isNavigating}
      isNoticeListLoading={noticesUiStore.isListLoading}
      isNoticeDetailLoading={noticesUiStore.isModalLoading}
      notices={noticesDomainStore.noticesList}
      createNoticeOnClick={() => {
        noticesDomainStore.resetEditableNotice();
        switchToAddNotice();
      }}
      editNoticeOnClick={() => {
        runInAction(() => (noticesUiStore.isEditLoading = true));
        switchToEditNotice(noticesDomainStore.editableNotice.id);
      }}
      viewNoticeOnClick={() =>
        switchToViewNotice(noticesDomainStore.editableNotice.id)
      }
      noticesLength={noticesUiStore.totalNotices}
      onPageChange={(pageNumber, sizePerPage, sortField, sortOrder) => {
        loadNotices.run(pageNumber, sizePerPage, sortField, sortOrder);
      }}
      onSort={(sortField, sortOrder, pageNumber, sizePerPage) => {
        loadNotices.run(pageNumber, sizePerPage, sortField, sortOrder);
      }}
      noticeDetail={noticesDomainStore.editableNotice}
      loadNoticeDetail={loadEditableNotice.run}
      currentPageNumber={noticesUiStore.currentNoticeListPageNumber}
      currentSortField={noticesUiStore.currentNoticeListSortField}
      currentSortOrder={noticesUiStore.currentNoticeListSortOrder}
      actions={actions}
      onActionSelected={onActionSelected}
      selectedNotice={selectedNotice}
      actionFilterFn={actionFilterFn}
    />
  );
};

export default observer(ListNotices);
